import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { wfsCache, debounce, bboxOverlap, requestManager } from '../../utils/wfsCache';
import { createSingleDrillHolePopupTable } from './DrillHoleOverlay';
import {
    Popup,
    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';

const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
    timeout: 30000
});

// Debounced query function
export const debouncedQueryDHWFS = debounce(queryDHWFSOptimized, 300);

/**
 * Optimized drill hole WFS query with caching and request management
 */
export function queryDHWFSOptimized(bboxString, setStateGeoJSON, dhFilters, retryCount = 0) {
    const cacheKey = wfsCache.generateKey(bboxString, dhFilters, 'drillholes');
    
    // Check cache first
    const cachedResult = wfsCache.get(cacheKey);
    if (cachedResult) {
        setStateGeoJSON(cachedResult);
        return Promise.resolve(cachedResult);
    }

    // Create abort controller for this request
    const controller = requestManager.createController('drillholes');

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and
        au_gpt BETWEEN ${dhFilters.gptmt} AND ${dhFilters.gptlt} 
        and todepth <= ${dhFilters.maxDepth}
    `;

    const maxRetries = 10;
    const baseDelay = 1000;

    return GoldFinderGeoserver.get("/ne/ows?", {
        signal: controller.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_drill_holes',
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    }).then((response) => {
        // Process the data more efficiently with deduplication
        const features = response.data.features || [];
        const processedFeatures = new Map();
        
        // Use Map for O(1) lookups instead of array.find() for deduplication
        features.forEach(feature => {
            const id = feature.properties.dhgeochemid;
            if (processedFeatures.has(id)) {
                const existing = processedFeatures.get(id);
                existing.properties.au_gpt = +existing.properties.au_gpt + +feature.properties.au_gpt;
            } else {
                processedFeatures.set(id, feature);
            }
        });

        const result = {
            ...response.data,
            features: Array.from(processedFeatures.values())
        };

        // Cache the result
        wfsCache.set(cacheKey, result);
        setStateGeoJSON(result);
        return result;
    }).catch((error) => {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
            console.error('Drill hole WFS query error:', error);
        }
        
        // Don't re-throw canceled errors
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
            return null;
        }
        
        // Retry on timeout or network errors
        if ((error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') && retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retrying drill hole query in ${delay}ms (attempt ${retryCount + 1}/${maxRetries + 1})`);
            
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(queryDHWFSOptimized(bboxString, setStateGeoJSON, dhFilters, retryCount + 1));
                }, delay);
            });
        }
        
        // For other errors or max retries exceeded, return empty GeoJSON
        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        
        setStateGeoJSON(emptyResult);
        return emptyResult;
    });
}

/**
 * Optimized AssayLayers component with memoization
 */
function AssayLayersOptimized({ assayMarkerColourSettings, overlayVisibility }) {
    const orderedAssayMarkerArray = useMemo(() => 
        assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan),
        [assayMarkerColourSettings]
    );

    const layers = useMemo(() => {
        const layerConfigs = [];

        for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
            const next = i + 1;
            const config = orderedAssayMarkerArray[i];
            const layerId = config.id + '-dh';

            const layerConfig = {
                id: layerId,
                source: 'dhsamples',
                type: 'symbol',
                minzoom: 10.6,
                layout: { 
                    visibility: `${overlayVisibility.dh ? "visible" : "none"}`,
                    'icon-image': `triangle-${config.id}`,
                    'icon-size': 1.2,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'icon-anchor': 'center'
                },
                paint: {
                    'icon-opacity': 1.0
                },
                filter: ['all',
                    ['>=', 'au_gpt', config.moreThan],
                    ['<', 'au_gpt', i !== orderedAssayMarkerArray.length - 1 ? orderedAssayMarkerArray[next].moreThan : 9999]
                ]
            };

            layerConfigs.push(layerConfig);
        }

        return layerConfigs;
    }, [orderedAssayMarkerArray, overlayVisibility.dh]);

    return (
        <>
            {layers.map((layerConfig) => (
                <Layer key={layerConfig.id} {...layerConfig} />
            ))}
        </>
    );
}

/**
 * Optimized DrillHoleOverlay component with performance improvements
 */
export function DrillHoleOverlayMapLibreOptimized({ 
    overlayVisibility, 
    drillHoleMarkerColourSettings, 
    drillHoleGeoJson, 
    setDrillHoleGeoJson, 
    drillHoleFilters 
}) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "drillhole_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap();
    const lastBboxRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Optimized triangle image creation with memoization
    const createTriangleImages = useCallback(() => {
        if (!goldfindermap || !drillHoleMarkerColourSettings) return;
        
        drillHoleMarkerColourSettings.forEach((setting) => {
            const canvas = document.createElement('canvas');
            canvas.width = 24;
            canvas.height = 24;
            const ctx = canvas.getContext('2d');
            
            // Clear canvas with transparent background
            ctx.clearRect(0, 0, 24, 24);
            
            // Draw white border triangle first (slightly larger)
            ctx.beginPath();
            ctx.moveTo(12, 20); // bottom point
            ctx.lineTo(4, 4);   // top left
            ctx.lineTo(20, 4);  // top right
            ctx.closePath();
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            
            // Draw colored triangle on top
            ctx.beginPath();
            ctx.moveTo(12, 19); // bottom point
            ctx.lineTo(5, 5);   // top left
            ctx.lineTo(19, 5);  // top right
            ctx.closePath();
            ctx.fillStyle = setting.color;
            ctx.fill();
            
            const imageId = `triangle-${setting.id}`;
            
            // Convert canvas to PNG data URL
            const dataURL = canvas.toDataURL('image/png');
            const img = new Image();
            img.onload = () => {
                // Remove existing image if it exists
                if (goldfindermap.hasImage(imageId)) {
                    goldfindermap.removeImage(imageId);
                }
                goldfindermap.addImage(imageId, img);
            };
            img.src = dataURL;
        });
    }, [goldfindermap, drillHoleMarkerColourSettings]);

    // Create triangle images when map or settings change
    useEffect(() => {
        createTriangleImages();
        
        // Cleanup function
        return () => {
            if (goldfindermap && drillHoleMarkerColourSettings) {
                drillHoleMarkerColourSettings.forEach((setting) => {
                    const imageId = `triangle-${setting.id}`;
                    if (goldfindermap.hasImage(imageId)) {
                        goldfindermap.removeImage(imageId);
                    }
                });
            }
        };
    }, [createTriangleImages]);

    // Optimized query function
    const queryDrillHoles = useCallback(async (bbox) => {
        if (!goldfindermap || goldfindermap.getZoom() <= 10.6 || !overlayVisibility.dh) return;

        // Check if bbox has changed significantly to avoid redundant queries
        if (lastBboxRef.current && bboxOverlap(bbox, lastBboxRef.current)) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await debouncedQueryDHWFS(bbox, setDrillHoleGeoJson, drillHoleFilters);
            if (result !== null) {
                lastBboxRef.current = bbox;
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error querying drill holes:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [goldfindermap, drillHoleFilters, setDrillHoleGeoJson, overlayVisibility.dh]);

    // Effect for filter changes
    useEffect(() => {
        if (!goldfindermap) return;
        
        if (!overlayVisibility.dh) {
            // Clear data when overlay is disabled
            setDrillHoleGeoJson(emptyGeoJson);
            return;
        }
        
        // Clear cache when filters change
        wfsCache.clearLayer('drillholes');
        
        if (goldfindermap.getZoom() > 10.6) {
            const bounds = goldfindermap.getBounds();
            const wfsbbox = [
                bounds.getSouthWest().lng, 
                bounds.getSouthWest().lat, 
                bounds.getNorthEast().lng, 
                bounds.getNorthEast().lat
            ].join();
            
            queryDrillHoles(wfsbbox);
        }
    }, [drillHoleFilters, queryDrillHoles, overlayVisibility.dh]);

    // Map movement effect
    useEffect(() => {
        if (!goldfindermap) return;

        const handleMapUpdate = () => {
            if (goldfindermap.getZoom() > 10.6 && overlayVisibility.dh) {
                const bounds = goldfindermap.getBounds();
                const wfsbbox = [
                    bounds.getSouthWest().lng, 
                    bounds.getSouthWest().lat, 
                    bounds.getNorthEast().lng, 
                    bounds.getNorthEast().lat
                ].join();
                queryDrillHoles(wfsbbox);
            } else {
                // Clear data when zoomed out
                setDrillHoleGeoJson(emptyGeoJson);
            }
        };

        // Initial load
        handleMapUpdate();

        // Listen for map events with debouncing
        const debouncedUpdate = debounce(handleMapUpdate, 500);
        goldfindermap.on('moveend', debouncedUpdate);
        goldfindermap.on('zoomend', handleMapUpdate);

        return () => {
            goldfindermap.off('moveend', debouncedUpdate);
            goldfindermap.off('zoomend', handleMapUpdate);
        };
    }, [goldfindermap, queryDrillHoles, setDrillHoleGeoJson, overlayVisibility.dh]);

    return (
        overlayVisibility.dh && (
            <Source id="dhsamples" type="geojson" data={drillHoleGeoJson}>
                <AssayLayersOptimized 
                    overlayVisibility={overlayVisibility} 
                    assayMarkerColourSettings={drillHoleMarkerColourSettings} 
                />
            </Source>
        )
    );
}